import { FileText, ImageIcon, Loader2, Volume2, X } from 'lucide-react';
import { cn } from '@/utils/cn';
import { chatLucideIconProps } from '../chat-ui';
import type { ChatComposerAttachment } from './types';
import { getAttachmentMeta } from './utils';

function AttachmentIcon({
  attachment,
}: {
  attachment: ChatComposerAttachment;
}) {
  if (attachment.kind === 'image' && attachment.previewUrl) {
    return (
      <img
        src={attachment.previewUrl}
        alt=""
        className="size-10 rounded-xl object-cover"
      />
    );
  }

  if (attachment.kind === 'audio') {
    return (
      <div className="flex size-10 items-center justify-center rounded-xl bg-muted/70 text-muted-foreground">
        <Volume2 {...chatLucideIconProps} className="size-4" />
      </div>
    );
  }

  if (attachment.kind === 'image') {
    return (
      <div className="flex size-10 items-center justify-center rounded-xl bg-muted/70 text-muted-foreground">
        <ImageIcon {...chatLucideIconProps} className="size-4" />
      </div>
    );
  }

  return (
    <div className="flex size-10 items-center justify-center rounded-xl bg-muted/70 text-muted-foreground">
      <FileText {...chatLucideIconProps} className="size-4" />
    </div>
  );
}

export function ChatComposerAttachmentChip({
  attachment,
  disabled,
  onRemove,
}: {
  attachment: ChatComposerAttachment;
  disabled: boolean;
  onRemove: (id: string) => void;
}) {
  return (
    <div
      className={cn(
        'flex min-w-0 max-w-full items-center gap-3 rounded-2xl border border-border/80 bg-muted/25 px-2.5 py-2',
        attachment.status === 'failed' &&
          'border-rose-200/80 bg-rose-50/80 dark:border-rose-400/30 dark:bg-rose-500/10'
      )}
    >
      <AttachmentIcon attachment={attachment} />

      <div className="min-w-0">
        <div className="chat-type-body-sm truncate text-foreground">
          {attachment.name}
        </div>
        <div
          className={cn(
            'chat-type-caption truncate text-muted-foreground',
            attachment.status === 'failed' && 'text-rose-600 dark:text-rose-300'
          )}
        >
          {getAttachmentMeta(attachment)}
        </div>
      </div>

      <button
        type="button"
        className="flex size-7 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-black/6 hover:text-foreground disabled:pointer-events-none disabled:opacity-50 dark:hover:bg-white/8"
        onClick={() => onRemove(attachment.id)}
        disabled={disabled}
        aria-label={`Remove ${attachment.name}`}
      >
        {attachment.status === 'uploading' ? (
          <Loader2 {...chatLucideIconProps} className="size-4 animate-spin" />
        ) : (
          <X {...chatLucideIconProps} className="size-4" />
        )}
      </button>
    </div>
  );
}
